/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

const GAP_UNDER_TOOLBAR = 22;
const REMEASURE_DELAYS  = [0, 120, 400];

export interface ToolbarPanelAnchorProps {
  enabled: boolean;
  show?: boolean;
  children: ReactNode;
}

const getToolbarRow = (): HTMLElement | null => (
  document.querySelector('[data-map-tools-row]') as HTMLElement | null
);

/**
 * Anchors the map dialogs that open from the top-right tools row to that row. The dialogs sit in
 * a body level portal, whose position does not follow the row when the filter bar above the map
 * opens, closes or has a filter applied. This wrapper measures the row's bottom edge and moves
 * its dialogs to sit just under it, re-measuring on a resize and whenever the row, its parent or
 * the page changes size.
 *
 * ```typescript
 *
 *   import ToolbarPanelAnchor from '@ion/dls/src/maps/toolbar/ToolbarPanelAnchor';
 *   ...
 *
 *   <ToolbarPanelAnchor enabled={moveAllToolsToTop} show={isOpen}>
 *     <LayerChooser />
 *   </ToolbarPanelAnchor>
 *
 * ```
 *
 * @param enabled  `true` to anchor the dialogs, otherwise the children render as they are.
 * @param show     `true` while the dialog is open, which is when it is measured.
 * @param children The dialogs to anchor.
 */
export const ToolbarPanelAnchor = ({enabled, show = true, children}: ToolbarPanelAnchorProps) => {
  const ref               = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);

  const measure = useCallback(() => {
    const row    = getToolbarRow();
    const anchor = ref.current;
    if (row && anchor) {
      const applied = parseFloat(anchor.style.top) || 0;
      const natural = anchor.getBoundingClientRect().top - applied;
      setShift(Math.round(row.getBoundingClientRect().bottom + GAP_UNDER_TOOLBAR - natural));
    }
  }, []);

  useLayoutEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    let observer: ResizeObserver;
    if (enabled && show) {
      measure();
      REMEASURE_DELAYS.forEach(delay => timers.push(setTimeout(measure, delay)));
      window.addEventListener('resize', measure);
      const row = getToolbarRow();
      if (typeof ResizeObserver !== 'undefined' && row) {
        observer = new ResizeObserver(measure);
        [row, row.parentElement, document.body].forEach(node => node && observer.observe(node));
      }
    }
    return function() {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [enabled, show, measure]);

  return enabled
    ? <div ref={ref} style={{position: 'relative', top: shift}}>{children}</div>
    : <>{children}</>;
};

export default ToolbarPanelAnchor;
